import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  Music2,
  ListMusic,
  Disc3,
  User,
  WifiOff,
  X,
  Clock,
  TrendingUp,
} from "lucide-react";
import {
  getRoom,
  getQueue,
  searchKaraoke,
  searchYouTube,
  addToQueue,
  removeFromQueue,
  getPopularSearches,
} from "@/lib/api";
import { getSocket } from "@/lib/socket";
import { formatDuration, formatViews, decodeHtmlEntities } from "@/lib/format";
import { APP_TEXT_LOGO_URL } from "@/constants/branding";
import { clearSession } from "@/lib/session";

const RECENT_KEY = "openktv_recent_searches";
const RECENT_MAX = 8;
const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
};
const pushRecent = (term) => {
  const cleaned = term.trim();
  if (!cleaned) return;
  const list = readRecent().filter((t) => t.toLowerCase() !== cleaned.toLowerCase());
  list.unshift(cleaned);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
};

export default function GuestRoom() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const code = (roomCode || "").toUpperCase();

  const [guest, setGuest] = useState(null);
  const [room, setRoom] = useState(null);
  const [queue, setQueue] = useState([]);
  const [player, setPlayer] = useState({ state: "idle", current_time: 0, duration: 0 });
  const [tab, setTab] = useState("search");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchMeta, setSearchMeta] = useState({
    cacheHit: false,
    youtubeAvailable: true,
    totalLocalResults: 0,
  });
  const [youtubeSearching, setYoutubeSearching] = useState(false);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState({});
  const [socketConnected, setSocketConnected] = useState(false);
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState(readRecent());
  const searchAbort = useRef(null);
  const debounceTimer = useRef(null);

  // Load guest from storage
  useEffect(() => {
    const saved = localStorage.getItem(`guest_${code}`);
    if (!saved) {
      navigate(`/join/${code}`);
      return;
    }
    setGuest(JSON.parse(saved));
  }, [code, navigate]);

  // Load room + queue
  useEffect(() => {
    getRoom(code)
      .then(setRoom)
      .catch(() => {
        toast.error("Room not found");
        navigate("/");
      });
    getQueue(code).then((d) => setQueue(d.queue || []));
  }, [code, navigate]);

  // Socket connection
  useEffect(() => {
    if (!guest) return;
    const sock = getSocket();

    const onConnect = () => {
      setSocketConnected(true);
      sock.emit("guest:join", { room_code: code, guest_id: guest.guest_id });
    };
    const onDisconnect = () => setSocketConnected(false);
    const onQueueUpdate = (data) => setQueue(data.queue || []);
    const onPlayer = (data) => setPlayer(data || {});
    const onNotify = (n) => {
      if (n.type === "guest_joined") toast(`${n.name} joined`);
      if (n.type === "song_skipped") toast("Host skipped the song");
    };
    const onRoomClosed = (data) => {
      const reason = data?.reason || "closed";
      const msg =
        reason === "host_disconnected"
          ? "Host was disconnected — session ended"
          : reason === "host_idle"
          ? "Session ended — host was inactive"
          : reason === "host_manual"
          ? "Host closed the room"
          : "Host closed the room";
      toast.error(msg);
      localStorage.removeItem(`guest_${code}`);
      clearSession();
      setTimeout(() => navigate("/"), 1000);
    };
    const onKicked = (data) => {
      const reason = data?.reason === "banned" ? "banned" : "removed";
      toast.error(`You were ${reason} from the room`);
      localStorage.removeItem(`guest_${code}`);
      clearSession();
      setTimeout(() => navigate("/"), 800);
    };

    sock.on("connect", onConnect);
    sock.on("disconnect", onDisconnect);
    sock.on("queue:update", onQueueUpdate);
    sock.on("player:update", onPlayer);
    sock.on("notify", onNotify);
    sock.on("room:closed", onRoomClosed);
    sock.on("guest:kicked", onKicked);
    sock.on("guest:banned", onKicked);

    if (sock.connected) onConnect();

    return () => {
      sock.off("connect", onConnect);
      sock.off("disconnect", onDisconnect);
      sock.off("queue:update", onQueueUpdate);
      sock.off("player:update", onPlayer);
      sock.off("notify", onNotify);
      sock.off("room:closed", onRoomClosed);
      sock.off("guest:kicked", onKicked);
      sock.off("guest:banned", onKicked);
    };
  }, [guest, code, navigate]);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!query.trim()) {
      setResults([]);
      setSearchMeta({ cacheHit: false, youtubeAvailable: true, totalLocalResults: 0 });
      setSearching(false);
      setRecent(readRecent());
      return;
    }
    debounceTimer.current = setTimeout(() => {
      runSearch(query.trim());
    }, 400);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Load popular searches once
  useEffect(() => {
    getPopularSearches(8)
      .then((d) => setPopular(d.results || []))
      .catch(() => {});
  }, []);

  const runSearch = async (q) => {
    if (searchAbort.current) searchAbort.current.abort();
    const ac = new AbortController();
    searchAbort.current = ac;
    setSearching(true);
    try {
      const data = await searchKaraoke(q, ac.signal);
      setResults(data.results || []);
      setSearchMeta({
        cacheHit: !!data.cacheHit,
        youtubeAvailable: data.youtubeAvailable !== false,
        totalLocalResults: Number(data.totalLocalResults || 0),
      });
      pushRecent(q);
    } catch (e) {
      if (e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") {
        toast.error("Search failed");
      }
    } finally {
      setSearching(false);
    }
  };

  const runYouTubeSearch = async () => {
    const q = query.trim();
    if (!q) return;
    if (searchAbort.current) searchAbort.current.abort();
    const ac = new AbortController();
    searchAbort.current = ac;
    setYoutubeSearching(true);
    try {
      const data = await searchYouTube(q, ac.signal);
      setResults(data.results || []);
      setSearchMeta({
        cacheHit: false,
        youtubeAvailable: data.youtubeAvailable !== false,
        totalLocalResults: 0,
      });
    } catch (e) {
      if (e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") {
        toast.error("YouTube search failed");
      }
    } finally {
      setYoutubeSearching(false);
    }
  };

  const handleAdd = async (r) => {
    if (!guest) return;
    setAdding((s) => ({ ...s, [r.video_id]: true }));
    try {
      await addToQueue(code, {
        video_id: r.video_id,
        title: r.title,
        channel: r.channel,
        thumbnail: r.thumbnail,
        duration: r.duration,
        source: r.source,
        requested_by: guest.guest_id,
        requested_by_name: guest.nickname,
      });
      toast.success("Added to queue");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setAdding((s) => ({ ...s, [r.video_id]: false }));
    }
  };

  const handleRemoveMine = async (id) => {
    try {
      await removeFromQueue(code, id, { guest_id: guest.guest_id });
      toast.success("Removed");
    } catch (err) {
      toast.error("Cannot remove");
    }
  };

  if (!guest) return null;

  const nowPlaying = queue[0];
  const upcoming = queue.slice(1);

  return (
    <div className="ambient-bg min-h-screen relative pb-24" data-testid="guest-room-page">
      <div className="grain" />
      {/* Header */}
      <header className="relative z-10 px-3 sm:px-4 pt-4 sm:pt-5 pb-3 flex items-center gap-2 sm:gap-3 sticky top-0 glass border-b border-white/5">
        <div className="w-28 h-10 sm:w-32 sm:h-11 flex-shrink-0">
          <img
            src={APP_TEXT_LOGO_URL}
            alt="OpenKTV"
            className="w-full h-full object-contain object-left"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 leading-tight">
            {room?.room_name || "Room"}
          </div>
          <div
            className="text-white font-semibold truncate font-[Outfit]"
            data-testid="guest-nickname-display"
          >
            {guest.nickname}
          </div>
        </div>
        {!socketConnected && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px]">
            <WifiOff size={10} /> Offline
          </div>
        )}
        <div className="text-right">
          <div className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-zinc-500 leading-tight">
            Code
          </div>
          <div
            className="text-purple-300 font-mono font-bold tracking-[0.18em] sm:tracking-wider text-sm sm:text-base"
            data-testid="guest-room-code"
          >
            {code}
          </div>
        </div>
      </header>

      {/* Main scrollable content */}
      <main className="relative z-10 px-3 sm:px-4 py-4 max-w-xl sm:max-w-2xl lg:max-w-3xl mx-auto">
        {tab === "search" && (
          <SearchTab
            query={query}
            setQuery={setQuery}
            results={results}
            searchMeta={searchMeta}
            searching={searching}
            youtubeSearching={youtubeSearching}
            onAdd={handleAdd}
            onSearchYouTube={runYouTubeSearch}
            adding={adding}
            popular={popular}
            recent={recent}
            onClearRecent={() => {
              localStorage.removeItem(RECENT_KEY);
              setRecent([]);
            }}
          />
        )}
        {tab === "queue" && (
          <QueueTab
            queue={upcoming}
            guestId={guest.guest_id}
            onRemove={handleRemoveMine}
          />
        )}
        {tab === "now" && <NowPlayingTab nowPlaying={nowPlaying} player={player} />}
        {tab === "profile" && (
          <ProfileTab guest={guest} room={room} code={code} navigate={navigate} />
        )}
      </main>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 h-20 glass border-t border-white/5 flex items-center justify-around px-2 pb-safe">
        <NavBtn
          icon={<Search size={22} />}
          label="Search"
          active={tab === "search"}
          onClick={() => setTab("search")}
          testid="nav-search"
        />
        <NavBtn
          icon={<ListMusic size={22} />}
          label="Queue"
          badge={upcoming.length}
          active={tab === "queue"}
          onClick={() => setTab("queue")}
          testid="nav-queue"
        />
        <NavBtn
          icon={<Disc3 size={22} className={nowPlaying ? "animate-spin" : ""} />}
          label="Playing"
          active={tab === "now"}
          onClick={() => setTab("now")}
          testid="nav-now-playing"
        />
        <NavBtn
          icon={<User size={22} />}
          label="Profile"
          active={tab === "profile"}
          onClick={() => setTab("profile")}
          testid="nav-profile"
        />
      </nav>
    </div>
  );
}

function NavBtn({ icon, label, active, onClick, badge, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-colors ${
        active
          ? "text-purple-400"
          : "text-zinc-500 hover:text-zinc-300"
      }`}
    >
      <div className="relative">
        {icon}
        {!!badge && (
          <span className="absolute -top-1.5 -right-2 bg-purple-500 text-white text-[10px] font-bold rounded-full px-1.5 py-px min-w-[16px] text-center">
            {badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium uppercase tracking-wider">
        {label}
      </span>
      {active && (
        <motion.span
          layoutId="nav-indicator"
          className="absolute -top-px left-1/2 -translate-x-1/2 w-8 h-0.5 bg-purple-400 rounded-full"
        />
      )}
    </button>
  );
}

function SearchTab({
  query,
  setQuery,
  results,
  searchMeta,
  searching,
  youtubeSearching,
  onAdd,
  onSearchYouTube,
  adding,
  popular,
  recent,
  onClearRecent,
}) {
  const showEmpty = !query && !searching;
  return (
    <div>
      <h2 className="text-2xl font-bold text-white font-[Outfit] mb-1">
        Find a song
      </h2>
      <p className="text-zinc-500 text-sm mb-5">
        We auto-append &ldquo;karaoke&rdquo; to every search.
      </p>

      <div className="relative mb-5 sticky top-[64px] sm:top-[68px] z-10">
        <Search
          size={18}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500"
        />
        <input
          data-testid="search-song-input"
          type="text"
          className="input-base pl-11 pr-10"
          placeholder="Song or artist..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <button
            data-testid="clear-search"
            onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-zinc-800 flex items-center justify-center text-zinc-500"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {searching && (
        <div className="flex items-center justify-center py-12 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={18} />
          Searching karaoke tracks...
        </div>
      )}

      {!searching && query && results.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <Music2 size={32} className="mx-auto mb-3 opacity-40" />
          <div>No karaoke results found.</div>
        </div>
      )}

      {!searching && searchMeta.cacheHit && searchMeta.totalLocalResults > 0 && (
        <div className="mt-3 mb-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
          <div className="text-emerald-200 font-semibold text-sm">
            Found {searchMeta.totalLocalResults} songs in OpenKTV Catalog
          </div>
        </div>
      )}

      {showEmpty && (
        <div className="space-y-6">
          {recent.length > 0 && (
            <section data-testid="recent-searches">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-widest">
                  <Clock size={12} />
                  Recent
                </div>
                <button
                  data-testid="clear-recent-btn"
                  onClick={onClearRecent}
                  className="text-zinc-500 hover:text-zinc-300 text-xs"
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {recent.map((term) => (
                  <button
                    key={term}
                    data-testid={`recent-chip-${term.replace(/\s+/g, "-")}`}
                    onClick={() => setQuery(term)}
                    className="px-3 py-1.5 rounded-full bg-zinc-900/70 border border-zinc-800 hover:border-purple-500/40 hover:bg-purple-500/10 text-sm text-zinc-200 transition-all"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </section>
          )}

          {popular.length > 0 && (
            <section data-testid="popular-searches">
              <div className="flex items-center gap-2 mb-2.5 text-zinc-400 text-xs uppercase tracking-widest">
                <TrendingUp size={12} />
                Popular this room
              </div>
              <div className="flex flex-wrap gap-2">
                {popular.map((p) => (
                  <button
                    key={p.term}
                    data-testid={`popular-chip-${p.term.replace(/\s+/g, "-")}`}
                    onClick={() => setQuery(p.term)}
                    className="px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 hover:border-purple-400/60 text-sm text-purple-200 transition-all flex items-center gap-1.5"
                  >
                    <span className="capitalize">{p.term}</span>
                    {p.count > 0 && (
                      <span className="text-[10px] text-purple-400/70">
                        · {p.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </section>
          )}

          {recent.length === 0 && popular.length === 0 && (
            <div className="text-center py-16">
              <div className="inline-block p-5 rounded-3xl bg-purple-500/10 border border-purple-500/20 mb-4">
                <Music2 size={36} className="text-purple-400" />
              </div>
              <div className="text-white font-semibold text-lg mb-1">
                Start typing
              </div>
              <div className="text-zinc-500 text-sm">
                Try &ldquo;Perfect&rdquo;, &ldquo;Bohemian Rhapsody&rdquo;,
                &ldquo;BTS&rdquo;
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2 mt-4">
        <AnimatePresence>
          {results.map((r, idx) => (
            <motion.div
              key={r.video_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(idx * 0.03, 0.3) }}
              className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3 p-2.5 rounded-2xl bg-zinc-900/60 border border-white/5 hover:border-purple-500/30"
              data-testid={`search-result-${r.video_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium leading-tight line-clamp-2">
                  {decodeHtmlEntities(r.title)}
                </div>
                <div className="text-zinc-500 text-xs mt-0.5 truncate">
                  {decodeHtmlEntities(r.channel)} · {formatDuration(r.duration)} ·{" "}
                  {formatViews(r.view_count)} views
                </div>
              </div>
              <button
                data-testid={`add-song-${r.video_id}`}
                disabled={adding[r.video_id]}
                onClick={() => onAdd(r)}
                className="w-full sm:w-auto px-3 py-2 sm:py-1.5 rounded-lg bg-purple-500 hover:bg-purple-400 text-white text-sm font-medium flex items-center justify-center transition-all flex-shrink-0 disabled:opacity-50"
              >
                {adding[r.video_id] ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 size={14} className="animate-spin" />
                    Adding...
                  </span>
                ) : (
                  "Add to Queue"
                )}
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {!searching && searchMeta.cacheHit && results.length > 0 && (
        <div className="mt-5 rounded-2xl border border-white/10 bg-zinc-900/50 px-4 py-4">
          <div className="text-zinc-300 text-sm mb-3">Can&apos;t find your song?</div>
          <button
            type="button"
            data-testid="search-youtube-btn"
            onClick={onSearchYouTube}
            disabled={youtubeSearching}
            className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-60 text-white text-sm font-medium"
          >
            {youtubeSearching ? "Searching YouTube..." : "Search YouTube"}
          </button>
        </div>
      )}
    </div>
  );
}

function QueueTab({ queue, guestId, onRemove }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-white font-[Outfit] mb-1">
        Up Next
      </h2>
      <p className="text-zinc-500 text-sm mb-5">
        {queue.length} song{queue.length !== 1 ? "s" : ""} waiting
      </p>

      {queue.length === 0 ? (
        <div className="text-center py-20 text-zinc-500">
          <ListMusic size={36} className="mx-auto mb-3 opacity-40" />
          <div>Queue is empty</div>
          <div className="text-xs text-zinc-600 mt-1">
            Be the first to add a song!
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="guest-queue-list">
          <AnimatePresence>
            {queue.map((q, idx) => (
              <motion.div
                layout
                key={q.id}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 16 }}
                className="flex items-center gap-2 sm:gap-2.5 p-2.5 rounded-2xl bg-zinc-900/60 border border-white/5"
              >
                <div className="w-7 h-7 rounded-full bg-zinc-800 text-zinc-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">
                    {decodeHtmlEntities(q.title)}
                  </div>
                  <div className="text-zinc-500 text-xs truncate">
                    by {q.requested_by_name} · {formatDuration(q.duration)}
                  </div>
                </div>
                {q.requested_by === guestId && (
                  <button
                    data-testid={`remove-my-song-${q.id}`}
                    onClick={() => onRemove(q.id)}
                    className="text-zinc-500 hover:text-red-400 text-xl w-8 h-8 rounded-md hover:bg-zinc-800/70"
                  >
                    ×
                  </button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function NowPlayingTab({ nowPlaying, player }) {
  if (!nowPlaying) {
    return (
      <div className="text-center py-24">
        <Disc3 size={48} className="mx-auto mb-4 text-zinc-700" />
        <div className="text-white text-xl font-semibold font-[Outfit] mb-1">
          Nothing playing
        </div>
        <div className="text-zinc-500 text-sm">
          Add a song from the Search tab.
        </div>
      </div>
    );
  }
  const pct = player.duration
    ? Math.min(100, (player.current_time / player.duration) * 100)
    : 0;
  return (
    <div>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-[10px] uppercase tracking-widest mb-4">
          <Disc3 size={11} className="animate-spin" /> Now Playing
        </div>
      </div>
      <div className="rounded-3xl overflow-hidden glass mb-6">
        <div className="relative aspect-video bg-zinc-900">
          <img
            src={nowPlaying.thumbnail}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/95 via-zinc-950/40 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5">
            <div className="text-white font-bold text-lg sm:text-xl line-clamp-2 font-[Outfit] drop-shadow-lg">
              {nowPlaying.title}
            </div>
            <div className="text-zinc-300 text-xs sm:text-sm mt-1 truncate">
              {nowPlaying.channel}
            </div>
          </div>
        </div>
        <div className="px-5 py-4">
          <div className="flex items-center justify-between text-xs text-zinc-400 mb-2">
            <span>{formatDuration(player.current_time)}</span>
            <span>{formatDuration(player.duration || nowPlaying.duration)}</span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-4 text-center text-sm text-zinc-400">
            Requested by{" "}
            <span className="text-purple-300 font-semibold">
              {nowPlaying.requested_by_name}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ guest, room, code, navigate }) {
  const leaveRoom = () => {
    localStorage.removeItem(`guest_${code}`);
    navigate("/");
  };
  return (
    <div>
      <h2 className="text-2xl font-bold text-white font-[Outfit] mb-5">
        Profile
      </h2>
      <div className="glass rounded-3xl p-6 mb-6 flex items-center gap-4">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl"
          style={{ background: guest.avatar_color }}
        >
          {guest.nickname.slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-xl font-[Outfit] truncate">
            {guest.nickname}
          </div>
          <div className="text-zinc-500 text-sm">Guest in {room?.room_name}</div>
        </div>
      </div>
      <div className="glass rounded-2xl p-5 mb-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          Room Code
        </div>
        <div className="text-2xl text-white font-bold font-[Outfit] tracking-[0.25em]">
          {code}
        </div>
      </div>
      <button
        data-testid="leave-room-btn"
        onClick={leaveRoom}
        className="btn-secondary w-full mt-6 text-red-300 hover:text-red-200"
      >
        Leave Room
      </button>
    </div>
  );
}
